import { Component, Input, ElementRef } from '@angular/core';
import * as Highcharts from 'highcharts';

import { BaseHighChartComponent } from './base-chart.component';
import { ChartConfigService } from '../services/chart-config.service';
import {
  buildScoreYAxisOptions,
  buildVolumeYAxisOptions,
} from '../utils/y-axis-config.util';

/**
 * CombinationChartComponent supports multiple series types in a single chart.
 * Each series in chartData should specify its own `type` property.
 *
 * @example
 * ```html
 * <app-combination-chart
 *   [chartData]="chartData"
 *   [dualYAxis]="true"
 *   [scoreType]="'sentiment'"
 * ></app-combination-chart>
 * ```
 */
@Component({
  selector: 'app-combination-chart',
  template:
    '<div class="chart-container" style="height: 100%; width: 100%;"></div>',
})
export class CombinationChartComponent extends BaseHighChartComponent {
  constructor(
    elementRef: ElementRef,
    protected configService: ChartConfigService,
  ) {
    super(elementRef, configService);
  }

  // ============================================================================
  // Essential Inputs
  // ============================================================================

  @Input() showLegend: boolean = true;
  @Input() enableZoom: boolean = true;
  @Input() tooltipOptions: Highcharts.TooltipOptions = {};
  @Input() dualYAxis: boolean = false;

  @Input() scoreType: string = '';
  //title inputs
  @Input() chartTitle: string = '';
  @Input() yAxisTitle: string = '';
  @Input() secondYAxisTitle: string = '';
  @Input() xAxisTitle: string = '';

  //x-axis configuration
  @Input() xAxisType: 'datetime' | 'category' = 'datetime';
  @Input() xAxisCategories: string[] = [];
  @Input() xAxisOrdinal: boolean = false;

  // Styling inputs with dark theme defaults
  @Input() backgroundColor: string = 'transparent';
  @Input() yAxisTitleColor: string = '#b8b8b8';
  @Input() yAxisLabelColor: string = '#b8b8b8';
  @Input() xAxisLabelColor: string = '#b8b8b8';

  @Input() yAxisCompactNumbers: boolean = true;

  protected override getChartConfig(): Highcharts.Options {
    const baseConfig = this.configService.getLineChartConfig();
    const xAxis = this.buildXAxisConfig(baseConfig);
    const yAxis = this.buildYAxisConfig(baseConfig);
    return {
      ...baseConfig,
      chart: {
        ...baseConfig.chart,
        type: undefined,
        zooming: { type: this.enableZoom ? 'x' : undefined },
        alignTicks: false,
        backgroundColor:
          this.backgroundColor || baseConfig.chart?.backgroundColor,
      },
      title: { text: this.chartTitle || undefined },
      navigator: { enabled: false },
      rangeSelector: { enabled: false },
      scrollbar: { enabled: false },
      series: this.chartData,
      colors:
        this.colorPalette.length > 0 ? this.colorPalette : baseConfig.colors,
      xAxis,
      yAxis,
      tooltip: { ...baseConfig.tooltip, ...this.tooltipOptions },
      legend: { ...baseConfig.legend, enabled: this.showLegend },
      plotOptions: this.buildPlotOptions(baseConfig),
    };
  }

  private buildXAxisConfig(
    baseConfig: Highcharts.Options,
  ): Highcharts.XAxisOptions {
    const baseLabelStyle = (baseConfig.xAxis as Highcharts.XAxisOptions).labels
      ?.style;
    const labelStyle = this.xAxisLabelColor
      ? { ...baseLabelStyle, color: this.xAxisLabelColor }
      : baseLabelStyle;

    if (this.xAxisType === 'category') {
      return {
        type: 'category',
        categories: this.xAxisCategories,
        title: { text: this.xAxisTitle },
        lineColor: (baseConfig.xAxis as Highcharts.XAxisOptions).lineColor,
        tickColor: (baseConfig.xAxis as Highcharts.XAxisOptions).tickColor,
        labels: { style: labelStyle },
      };
    }

    // DateTime axis (default)
    return {
      ...(baseConfig.xAxis as Highcharts.XAxisOptions),
      type: 'datetime',
      ordinal: this.xAxisOrdinal,
      title: { text: this.xAxisTitle },
      labels: {
        style: labelStyle,
        formatter: function () {
          const range =
            ((this.axis as Highcharts.Axis).max || 0) -
            ((this.axis as Highcharts.Axis).min || 0);
          const days = range / (24 * 3600 * 1000);
          return days > 60
            ? Highcharts.dateFormat("%b '%y", this.value as number)
            : Highcharts.dateFormat('%e %b', this.value as number);
        },
        rotation: 0,
        align: 'center',
      },
      gridLineColor: 'rgba(255, 255, 255, 0.05)',
      gridLineWidth: 0,
    };
  }

  private buildYAxisConfig(
    baseConfig: Highcharts.Options,
  ): Highcharts.YAxisOptions | Highcharts.YAxisOptions[] {
    const titleStyle = this.yAxisTitleColor
      ? { color: this.yAxisTitleColor }
      : undefined;
    const labelStyle = this.yAxisLabelColor
      ? { color: this.yAxisLabelColor }
      : (baseConfig.yAxis as Highcharts.YAxisOptions).labels?.style;

    const primaryYAxis = buildVolumeYAxisOptions(this.yAxisTitle);
    primaryYAxis.labels = {
      ...primaryYAxis.labels,
      style: labelStyle,
      ...(this.yAxisCompactNumbers && {
        formatter: function () {
          const value = this.value as number;
          if (Math.abs(value) >= 1000000) {
            return (value / 1000000).toFixed(1) + 'M';
          }
          if (Math.abs(value) >= 1000) {
            return (value / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
          }
          return String(value);
        },
      }),
    };
    if (titleStyle) {
      primaryYAxis.title = { ...primaryYAxis.title, style: titleStyle };
    }

    if (!this.dualYAxis) {
      return primaryYAxis;
    }

    const secondaryYAxis = buildScoreYAxisOptions(
      this.scoreType,
      this.secondYAxisTitle,
      true,
    );
    secondaryYAxis.labels = {
      ...secondaryYAxis.labels,
      style: labelStyle,
    };
    if (titleStyle) {
      secondaryYAxis.title = { ...secondaryYAxis.title, style: titleStyle };
    }

    return [primaryYAxis, secondaryYAxis];
  }

  private buildPlotOptions(
    baseConfig: Highcharts.Options,
  ): Highcharts.PlotOptions {
    return {
      ...baseConfig.plotOptions,
      series: {
        ...(baseConfig.plotOptions?.series as Highcharts.PlotSeriesOptions),
        dataGrouping: { enabled: true },
      },
      column: {
        grouping: true,
        borderWidth: 0,
        borderRadius: 0,
        pointPadding: 0.1,
        groupPadding: 0.2,
        dataGrouping: {
          enabled: true,
          approximation: 'sum',
          groupPixelWidth: 10,
        },
      },
      bar: {
        grouping: true,
        borderWidth: 0,
        borderRadius: 0,
        dataGrouping: { enabled: true },
      },
      line: {
        dataGrouping: {
          enabled: true,
          approximation: 'average',
          groupPixelWidth: 10,
        },
        marker: {
          enabled: false,
          radius: 2,
          states: { hover: { enabled: true, radius: 4 } },
        },
      },
      area: {
        dataGrouping: {
          enabled: true,
          approximation: 'average',
          groupPixelWidth: 10,
        },
        marker: { enabled: false },
      },
    };
  }
}
